import React, { useEffect } from 'react';
import {
  Edit,
  SimpleForm,
  FileInput,
  FileField,
  ImageField,
  FormDataConsumer,
  useRecordContext,
  useUpdate,
  useNotify,
  useRedirect,
  Toolbar,
  SaveButton,
  required
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
import { blocksToHtml, htmlToBlocks } from '../../../helpers/htmlUtils';
import { uploadImage } from '../../../config/services/strapi/strapi';

const getPreviewUrl = (file: any) => {
  const raw =
    file?.src ||
    file?.url ||
    file?.data?.attributes?.url ||
    file?.attributes?.url ||
    file?.formats?.thumbnail?.url ||
    file?.formats?.small?.url ||
    file?.formats?.medium?.url ||
    file?.formats?.large?.url ||
    '';

  if (!raw) return '';
  if (raw.startsWith('http') || raw.startsWith('blob:') || raw.startsWith('data:')) return raw;

  const base = process.env.REACT_APP_STRAPI_ENDPOINT || 'http://localhost:1337';
  return `${base}${raw}`;
};

const toInputMedia = (file: any) => {
  if (!file) return null;

  const src = getPreviewUrl(file);
  const title = file?.title || file?.name || file?.alternativeText || 'archivo';
  if (!src) return null;

  return {
    ...file,
    src,
    title,
  };
};

const normalizeArchivoForInput = (value: any) => {
  if (!value) return value;
  if (Array.isArray(value)) {
    const first = value[0];
    return toInputMedia(first) || first;
  }

  return toInputMedia(value) || value;
};

const normalizeHeroForInput = (value: any) => {
  if (!value) return value;
  if (!Array.isArray(value)) {
    const normalized = toInputMedia(value);
    return normalized ? [normalized] : value;
  }

  return value.map((item: any) => toInputMedia(item) || item);
};

const getJwtOrNotify = (notify: any, message: string) => {
  const jwt = localStorage.getItem('strapi_jwt');
  if (!jwt) {
    notify(message, { type: 'warning' });
    return null;
  }
  return jwt;
};

const uploadArchivo = async (archivo: any, notify: any) => {
  if (!archivo?.rawFile) return null;
  const jwt = getJwtOrNotify(notify, 'No autorizado para subir archivos');
  if (!jwt) return null;
  const up = await uploadImage(archivo.rawFile, jwt);
  return up?.id ? { id: up.id } : null;
};

const uploadHero = async (hero: any, notify: any) => {
  if (!Array.isArray(hero) || !hero.length) return [];
  const jwt = getJwtOrNotify(notify, 'No autorizado para subir imágenes');
  if (!jwt) return [];

  const uploadedIds: any[] = [];
  for (const item of hero) {
    if (!item?.rawFile) continue;
    const up = await uploadImage(item.rawFile, jwt);
    if (up?.id) uploadedIds.push({ id: up.id });
  }
  return uploadedIds;
};

const extractArchivoInfo = (value: any) => {
  if (!value) return null;
  const source = Array.isArray(value) ? value[0] : value;
  const nested = source?.data?.attributes || source?.attributes || source;
  if (!nested) return null;
  const name = nested?.name || source?.name || nested?.title || nested?.file?.name || source?.title || '';
  const url = nested?.url || source?.url || nested?.src || source?.src || '';
  if (!name && !url) return null;
  return { name, url };
};

function ArchivoTituloSincronizado() {
  const record = useRecordContext();

  return (
    <FormDataConsumer>
      {({ formData }) => {
        const fromForm = extractArchivoInfo(formData?.archivo);
        const fromRecord = extractArchivoInfo(record?.archivo);
        const archivo = fromForm || fromRecord;

        if (!archivo) {
          return <div style={{ marginTop: 8 }}>Titulo del archivo: No hay archivo</div>;
        }

        const title = archivo.name || 'Documento sin nombre';
        return (
          <div style={{ marginTop: 8 }}>
            Titulo del archivo:{' '}
            {archivo.url ? (
              <a href={archivo.url} target="_blank" rel="noreferrer">{title}</a>
            ) : (
              <span>{title}</span>
            )}
          </div>
        );
      }}
    </FormDataConsumer>
  );
}

function PublicidadHistoriaEdit(props: any) {
  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();
  const resourceName = 'fg-publicidadess';

  useEffect(() => {
    document.title = 'Editar Publicidad Historia';
  }, []);

  const handleSubmit = async (data: any) => {
    try {
      const payload: Record<string, any> = {
        descripcion: data.descripcion,
        detalle: data.detalle,
      };

      const archivo = await uploadArchivo(data.archivo, notify);
      if (data.archivo?.rawFile && !archivo) return;
      if (archivo) payload.archivo = archivo;

      const hero = await uploadHero(data.hero, notify);
      if (Array.isArray(data.hero) && data.hero.length && !hero.length) return;
      if (hero.length) payload.hero = hero;

      update(resourceName, { id: data.id, data: payload }, {
        onSuccess: () => {
          notify('Publicidad actualizada', { type: 'success' });
          redirect('list', resourceName);
        },
        onError: (err: any) => {
          notify(`Error: ${err?.message || err}`, { type: 'error' });
        }
      });
    } catch (e) {
      console.error(e);
      notify('Error al actualizar', { type: 'error' });
    }
  };

  return (
    <Edit {...props}>
      <SimpleForm onSubmit={handleSubmit} toolbar={<Toolbar><SaveButton alwaysEnable /></Toolbar>}>
        <RichTextInput
          source="descripcion"
          label="Descripción"
          format={blocksToHtml}
          parse={htmlToBlocks}
          validate={[required()]}
          fullWidth
        />

        <RichTextInput
          source="detalle"
          label="Detalle"
          format={blocksToHtml}
          parse={htmlToBlocks}
          fullWidth
        />

        <FileInput source="archivo" label="Archivo (descarga)" format={normalizeArchivoForInput}>
          <FileField source="src" title="title" />
        </FileInput>

        <ArchivoTituloSincronizado />

        <FileInput source="hero" label="Hero (imágenes)" multiple format={normalizeHeroForInput}>
          <ImageField source="src" title="title" />
        </FileInput>
      </SimpleForm>
    </Edit>
  );
}

export default PublicidadHistoriaEdit;
